import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getBidderByUuid } from "services";
import {
  findFirstPendingStep,
  determineKycType,
  getNonIndividualStepIndex,
  getStepTitles,
} from "./utils/kycStepUtils";
import { useRequiresApproverDetails } from "./utils/useRequiresApproverDetails";

import HorizontalStepper from "./HorizontalStepper";
import PaymentValidationApplicationForm from "./components/PaymentValidationApplicationForm";
import NonIndividualOfflineKycStepContent from "./components/NonIndividualOfflineKycStepContent";
import NonIndividualOnlineKycStepContent from "./components/NonIndividualOnlineKycStepContent";
import KYCBackButtonWithListingId from "./components/KYCBackButtonWithListingId";

interface NonIndividualKycWrapperProps {
  bidderUuid: any;
}

export default function NonIndividualKycWrapper({
  bidderUuid,
}: NonIndividualKycWrapperProps) {
  const [loading, setLoading] = useState(false);
  const [bidder, setBidder] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(-1);
  const [kycMode, setKycMode] = useState<any>("");
  const [offlinePaymentValidation, setOfflinePaymentValidation] =
    useState(false);
  const [offlinePaymentValidationState, setOfflinePaymentValidationState] =
    useState("pending");

  // Get step titles based on KYC mode using useMemo
  const stepsTitles = useMemo(
    () => getStepTitles("non_individual", kycMode || "online"),
    [kycMode],
  );
  const stepsTitlesOffline = useMemo(
    () => getStepTitles("non_individual", "offline"),
    [],
  );
  const requiresApproverDetails = useRequiresApproverDetails(bidder, kycMode);
  const offlineStepperTitles = useMemo(() => {
    if (requiresApproverDetails) {
      return stepsTitlesOffline;
    }
    return stepsTitlesOffline.filter((step) => step.value !== "approver_details");
  }, [requiresApproverDetails, stepsTitlesOffline]);

  // Function to recalculate and set the correct step based on current bidder data
  const recalculateAndSetStep = useCallback(() => {
    if (!bidder || !kycMode) return;

    const kycType = determineKycType(bidder);
    const accessType = bidder?.access_type;
    const step = findFirstPendingStep(
      bidder?.meta?.steps_status,
      kycType,
      kycMode,
      accessType,
    );
    console.log("step: ", step);

    const calculatedStep = getNonIndividualStepIndex(step, kycMode);
    console.log("calculatedStep: ", calculatedStep);

    // Only update if the calculated step is different from current activeStep
    if (calculatedStep !== activeStep && calculatedStep !== -1) {
      setActiveStep(calculatedStep);
    }
  }, [bidder, kycMode, activeStep]);

  const init = useCallback(async (bidderUuid: any) => {
    setLoading(true);
    if (bidderUuid) {
      try {
        const data: any = await getBidderByUuid(bidderUuid);
        setBidder(data);
        let mode = data?.meta?.bid_application_flow_mode
          ? data?.meta?.bid_application_flow_mode
          : "online";
        if (process.env.NEXT_PUBLIC_ENABLE_OFFLINE_NI_KYC !== "true") {
          mode = "online";
        }
        setKycMode(mode);
        if (
          process.env.NEXT_PUBLIC_ENABLE_ONLINE_DD_VALIDATION_FLOW === "true"
        ) {
          if (data?.meta?.enable_offline_payment_validation === true) {
            setOfflinePaymentValidation(true);
            if (data?.payment_data?.form_submission !== "submitted") {
              setOfflinePaymentValidationState("pending");
            } else {
              setOfflinePaymentValidationState("submitted");
            }
          }
        }

        // Calculate initial step using the utility function
        if (mode === "offline" || mode === "online") {
          const kycType = determineKycType(data);
          const accessType = data?.access_type;
          const step = findFirstPendingStep(
            data?.meta?.steps_status,
            kycType,
            mode,
            accessType,
          );

          // Map to step index using helper function
          const calculatedStep = getNonIndividualStepIndex(step, mode);
          setActiveStep(calculatedStep);
        }
      } catch (error) {
        console.log("error: ", error);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    init(bidderUuid);
  }, [bidderUuid, init]);

  // Recalculate step when bidder data changes (after updates)
  useEffect(() => {
    if (bidder && kycMode && !loading && activeStep !== -1) {
      // Use a small delay to ensure state updates are complete
      const timeoutId = setTimeout(() => {
        recalculateAndSetStep();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidder?.meta?.steps_status, kycMode, loading]);
  const enableUpdatedKyc =
    process.env.NEXT_PUBLIC_ENABLE_UPDATED_NI_DIGIO_KYC === "true";

  // Wrapper function for init that includes step recalculation
  const initWithRecalculation = useCallback(
    async (uuid?: any) => {
      await init(uuid || bidderUuid);
      recalculateAndSetStep();
    },
    [init, bidderUuid, recalculateAndSetStep],
  );

  if (
    offlinePaymentValidation === true &&
    offlinePaymentValidationState === "pending"
  ) {
    return (
      <PaymentValidationApplicationForm
        bidder={bidder}
        bidderUuid={bidderUuid}
        init={init}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
      />
    );
  }
  console.log("kycMode:------>>>>> ", kycMode);
  return (
    <div>
      {loading ? (
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
        </div>
      ) : (
        <>
          <KYCBackButtonWithListingId
            bidderAuctionId={bidder?.meta?.event_id}
          />
          {kycMode === "offline" && (
            <div>
              <HorizontalStepper
                totalSteps={offlineStepperTitles.length}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                stepsTitles={offlineStepperTitles}
              />
              <NonIndividualOfflineKycStepContent
                activeStep={activeStep}
                bidderUuid={bidderUuid}
                bidder={bidder}
                setActiveStep={setActiveStep}
                init={initWithRecalculation}
                offlinePaymentValidation={offlinePaymentValidation}
                stepsTitles={offlineStepperTitles}
                enableUpdatedKyc={enableUpdatedKyc}
                requiresApproverDetails={requiresApproverDetails}
              />
            </div>
          )}
          {kycMode === "online" && (
            <div>
              <HorizontalStepper
                totalSteps={stepsTitles.length}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                stepsTitles={stepsTitles}
              />
              <NonIndividualOnlineKycStepContent
                activeStep={activeStep}
                bidderUuid={bidderUuid}
                bidder={bidder}
                setActiveStep={setActiveStep}
                init={initWithRecalculation}
                offlinePaymentValidation={offlinePaymentValidation}
                stepsTitles={stepsTitles}
                enableUpdatedKyc={enableUpdatedKyc}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
